/**
 * Cálculos antropométricos según la ficha de la nutrióloga.
 * Funciones puras: reciben números y devuelven resultados, sin estado ni UI.
 * Todas las medidas en cm (estatura, circunferencias) y kg (peso).
 */

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** Índice cintura/cadera. Riesgo alto: ♀ ≥ 0.85, ♂ ≥ 0.94. */
export function indiceCinturaCadera(cintura, cadera, sexo) {
  const c = num(cintura), h = num(cadera);
  if (!c || !h) return null;
  const icc = c / h;
  const umbral = sexo === 'masculino' ? 0.94 : 0.85;
  return {
    valor: icc.toFixed(2),
    riesgoAlto: icc >= umbral,
    umbral,
  };
}

/**
 * Complexión = estatura / circunferencia de muñeca (ambas en cm).
 *   Hombres: Grande <9.6 · Mediana 9.6–10.4 · Chica >10.4
 *   Mujeres: Grande <9.9 · Mediana 9.9–10.9 · Chica >10.9
 */
export function complexion(estatura, muneca, sexo) {
  const e = num(estatura), m = num(muneca);
  if (!e || !m) return null;
  const r = e / m;
  let etiqueta;
  if (sexo === 'masculino') {
    etiqueta = r < 9.6 ? 'Grande' : r <= 10.4 ? 'Mediana' : 'Chica';
  } else {
    // por defecto se usa el rango femenino si no se especifica sexo
    etiqueta = r < 9.9 ? 'Grande' : r <= 10.9 ? 'Mediana' : 'Chica';
  }
  return { valor: r.toFixed(1), etiqueta };
}

/**
 * Interpretación del % de grasa corporal según OMS/Gallagher.
 * Retorna { label, color } o null si falta dato.
 */
export function interpretarGrasaCorporal(grasa, sexo) {
  const g = num(grasa);
  if (g === null) return null;
  if (sexo === 'masculino') {
    if (g <= 5)  return { label: 'No saludable', color: 'text-rose-600' };
    if (g <= 9)  return { label: 'Bajo',         color: 'text-amber-500' };
    if (g <= 20) return { label: 'Normal',        color: 'text-emerald-500' };
    if (g <= 25) return { label: 'Sobrepeso',     color: 'text-amber-500' };
    return             { label: 'Obesidad',       color: 'text-rose-600' };
  } else {
    if (g <= 8)  return { label: 'No saludable', color: 'text-rose-600' };
    if (g <= 19) return { label: 'Bajo',         color: 'text-amber-500' };
    if (g <= 30) return { label: 'Normal',        color: 'text-emerald-500' };
    if (g <= 35) return { label: 'Sobrepeso',     color: 'text-amber-500' };
    return             { label: 'Obesidad',       color: 'text-rose-600' };
  }
}

/**
 * Interpretación del % de masa muscular esquelética por sexo y grupo de edad.
 * masaMuscularPct: % guardado en la medición; edad: años.
 */
export function interpretarMasaMuscular(masaMuscularPct, sexo, edad) {
  const m = num(masaMuscularPct);
  const e = num(edad);
  if (m === null || e === null) return null;

  let rangos;
  if (sexo === 'masculino') {
    if (e <= 39)      rangos = [33.3, 39.3, 44.0];
    else if (e <= 59) rangos = [33.1, 39.1, 43.8];
    else              rangos = [32.9, 38.9, 43.6];
  } else {
    if (e <= 39)      rangos = [24.3, 30.3, 35.3];
    else if (e <= 59) rangos = [24.1, 30.1, 35.1];
    else              rangos = [23.9, 29.9, 34.9];
  }

  const [bajo, alto, muyAlto] = rangos;
  if (m < bajo)   return { label: 'Bajo',     color: 'text-amber-500' };
  if (m <= alto)  return { label: 'Normal',   color: 'text-emerald-500' };
  if (m <= muyAlto) return { label: 'Alto',   color: 'text-cyan-500' };
  return                   { label: 'Muy alto', color: 'text-cyan-600' };
}

/**
 * Peso ideal por estatura:
 *   ideal      = estatura(m)² × 22
 *   máx mujer  = estatura(m)² × 24
 *   máx hombre = estatura(m)² × 25
 */
export function pesoIdeal(estaturaCm, sexo) {
  const e = num(estaturaCm);
  if (!e) return null;
  const m2 = Math.pow(e / 100, 2);
  return {
    ideal: (m2 * 22).toFixed(1),
    maximo: (m2 * (sexo === 'masculino' ? 25 : 24)).toFixed(1),
  };
}
